/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { useField, useFormikContext } from "formik";
import {
  Box,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText,
  useMediaQuery,
  useTheme,
  ListItemText,
} from "@mui/material";

export type OpcionSelect = {
  readonly valor: string;
  readonly label: string | React.ReactNode;
  readonly color?: any
};

export type ItextInputProps = {
  readonly label: string;
  readonly type: string;
  readonly name: string;
  readonly InputProps?: any;
  readonly InputLabelProps?: any;
  readonly isCurrency?: boolean
  readonly disabled?: boolean;
  readonly id?: string;
  readonly opciones?: OpcionSelect[];
  readonly multiple?: boolean;
  readonly onChange?: (value: any)=> void;
  readonly toUpperCase?: boolean;
};

// Componente para el CheckBox
const RenderCheckbox = ({ field, meta, label, isMobile, ...props }: { field: any; meta: any; label: string; isMobile: boolean; [key: string]: any }) => {
  const mobileStyles = isMobile ? {
    flexDirection: 'row' as const,
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '15px',
    }
  } : {};
  
  const hasError = meta.touched && meta.error;
  
  return (
    <Box paddingY={isMobile ? 0.5 : 1} width="100%">
      <FormControlLabel
        control={<Checkbox {...field} {...props} size={isMobile ? "medium" : "small"} />}
        label={label}
        sx={mobileStyles}
      />
      {hasError && (
        <Box color="red" fontSize={isMobile ? 12 : 10} mt={0.5}>
          {meta.error}
        </Box>
      )}
    </Box>
  );
};

// Componente para Radio buttons
const RenderRadio = ({ 
  field, 
  meta, 
  label, 
  isMobile, 
  opciones, 
  name, 
  values, 
  handleRadioChange 
}: { 
  field: any; 
  meta: any; 
  label: string; 
  isMobile: boolean; 
  opciones?: OpcionSelect[]; 
  name: string;
  values: any;
  handleRadioChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const formControlStyles = {
    display: "flex", 
    flexDirection: isMobile ? 'column' : 'row',
    width: '100%',
    mb: isMobile ? 0.5 : 0
  } as const;

  const formLabelStyles = { 
    textAlign: 'left',
    mb: isMobile ? 0.5 : 0,
    fontSize: isMobile ? '15px' : 'inherit'
  } as const;

  const controlLabelStyles = isMobile ? { 
    margin: '4px 0',
    width: '100%',
    '& .MuiFormControlLabel-label': {
      fontSize: '15px',
    }
  } : {};
  
  const hasError = meta.touched && meta.error;
  
  return (
    <Box paddingY={isMobile ? 0.5 : 1} width="100%">
      <FormControl 
        component="fieldset" 
        sx={formControlStyles} 
        error={!!hasError}
      >
        <FormLabel component="legend" sx={formLabelStyles}>{label}</FormLabel>
        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"}
          width="100%"
        >
          {opciones?.map((option) => (
            <FormControlLabel
              key={option.valor}
              value={option.valor}
              control={
                <Radio
                  checked={values[name] === option.valor}
                  onChange={handleRadioChange}
                  value={option.valor}
                  color={option.color?? "default"}
                  name={name}
                  size="small"
                  inputProps={{ 'aria-label': option.label as string }}
                />
              }
              label={option.label}
              sx={controlLabelStyles}
            />
          ))}
        </Box>
        {hasError && (
          <FormHelperText sx={{ fontSize: isMobile ? 12 : 'inherit' }}>{meta.error}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

// Componente para Select
const RenderSelect = ({ 
  field, 
  meta, 
  label, 
  isMobile, 
  multiple, 
  opciones, 
  InputProps, 
  handleSelectChange
}: { 
  field: any; 
  meta: any; 
  label: string; 
  isMobile: boolean;
  multiple?: boolean;
  opciones?: OpcionSelect[];
  InputProps?: any;
  handleSelectChange?: (value: any) => void;
}) => {
  const inputLabelStyles = isMobile ? {
    fontSize: '15px',
    transform: 'translate(14px, 10px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  } : {};
  
  const inputStyles = isMobile ? {
    fontSize: '15px',
    padding: '0 14px',
    ...(InputProps?.sx ?? {})
  } : (InputProps?.sx ?? {});
  
  const menuItemStyles = isMobile ? {
    padding: '8px 16px',
    minHeight: '36px',
    fontSize: '15px'
  } : {};
  
  const outlinedInputStyles = isMobile ? {
    height: multiple ? 'auto' : '42px'
  } : {};
  
  const hasError = meta.touched && meta.error;
  
  // Función para renderizar el MenuItem según el tipo de select (múltiple o simple)
  const renderMenuItem = (option: OpcionSelect) => {
    if (multiple) {
      const isSelected = Array.isArray(field.value) && field.value.includes(option.valor);
      
      return (
        <MenuItem 
          key={option.valor} 
          value={option.valor}
          sx={{
            ...menuItemStyles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start'
          }}
        >
          <Checkbox 
            checked={isSelected} 
            size="small" 
            color="primary"
            sx={{ mr: 1, padding: '2px' }}
          />
          <ListItemText 
            primary={option.label} 
            sx={{ 
              m: 0,
              '& .MuiTypography-root': {
                fontSize: isMobile ? '15px' : 'inherit',
              }
            }}
          />
        </MenuItem>
      );
    }
    
    return (
      <MenuItem 
        key={option.valor} 
        value={option.valor} 
        onClick={() => handleSelectChange && handleSelectChange(option.valor)}
        sx={menuItemStyles}
      >
        {option.label}
      </MenuItem>
    );
  };
  
  // Función para mostrar las opciones seleccionadas en el select
  const renderValue = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return <em>{label}</em>;
    }
    
    if (Array.isArray(selected)) {
      // Para selección múltiple
      const selectedLabels = opciones
        ?.filter(option => selected.includes(option.valor))
        .map(option => option.label);
      
      // Si es un ReactNode, intentar convertirlo a string o mostrar una representación alternativa
      const renderedLabels = selectedLabels?.map(label => 
        typeof label === 'string' ? label : 'Seleccionado'
      );
      
      return renderedLabels?.join(', ');
    }
    
    // Para selección simple
    const selectedOption = opciones?.find(option => option.valor === selected);
    return selectedOption?.label || selected;
  };
  
  return (
    <TextField
      {...field}
      label={label}
      placeholder={label}
      autoComplete="off"
      error={!!hasError}
      helperText={hasError ? meta.error : ""}
      variant="outlined"
      size="small"
      sx={{ 
        textAlign: "left",
        marginBottom: isMobile ? 1 : 0,
        '& .MuiInputLabel-root': inputLabelStyles,
        '& .MuiFormHelperText-root': {
          fontSize: isMobile ? '12px' : 'inherit',
          marginLeft: 0,
          marginTop: 0
        },
        '& .MuiOutlinedInput-root': outlinedInputStyles
      }}
      InputProps={{
        ...InputProps,
        sx: inputStyles
      }}
      SelectProps={{
        multiple: multiple,
        renderValue: multiple ? renderValue : undefined,
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: isMobile ? '50vh' : 'auto',
              width: isMobile ? '90vw' : 'auto'
            }
          }
        }
      }}
      fullWidth
      select
    >
      {opciones?.map(option => renderMenuItem(option))}
    </TextField>
  );
};

// Componente para TextField normal
const RenderTextField = ({ 
  field, 
  meta, 
  label, 
  isMobile, 
  type,
  toUpperCase,
  InputProps,
  InputLabelProps,
  handleChange
}: { 
  field: any; 
  meta: any; 
  label: string; 
  isMobile: boolean;
  type: string;
  toUpperCase?: boolean;
  InputProps?: any;
  InputLabelProps?: any;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const isTextarea = type === "textarea";
  const isDateField = type === "date";
  const hasError = meta.touched && meta.error;
  
  const inputStyles = {
    ...(isMobile && {
      fontSize: '15px',
      padding: isTextarea ? '8px 14px' : '0',
      '.MuiInputBase-input': {
        padding: '10px 14px',
      }
    }),
    ...(InputProps?.sx ?? {})
  };
  
  const inputLabelStyles = {
    ...(isMobile && {
      fontSize: '15px',
      transform: 'translate(14px, 10px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)'
      }
    }),
    ...(InputLabelProps?.sx ?? {})
  };
  
  const textFieldStyles = {
    marginBottom: isMobile ? 1 : 0,
    '& .MuiFormHelperText-root': {
      fontSize: isMobile ? '12px' : 'inherit',
      marginLeft: 0,
      marginTop: 0
    },
    '& .MuiOutlinedInput-root': {
      ...(isMobile && {
        height: isTextarea ? 'auto' : '42px',
      })
    }
  };
  
  // Determine the onChange handler to use
  const changeHandler = (type === "text" && toUpperCase) ? handleChange : field.onChange;
  let textareaRows = undefined;
  if (isTextarea) {
    textareaRows = isMobile ? 3 : 5;
  }
  return (
    <TextField
      {...field}
      label={label}
      type={type}
      autoComplete="off"
      multiline={isTextarea}
      rows={textareaRows} // Fixed 'ows' to 'rows'
      error={!!hasError}
      helperText={hasError ? meta.error : ""}
      variant="outlined"
      size="small"
      fullWidth
      InputProps={{
        ...InputProps,
        sx: inputStyles
      }}
      InputLabelProps={{
        ...InputLabelProps,
        shrink: isDateField ? true : InputLabelProps?.shrink,
        sx: inputLabelStyles
      }}
      sx={textFieldStyles}
      onChange={changeHandler}
    />
  );
};

function TextInput({ label, onChange, toUpperCase = false, ...props }: Readonly<ItextInputProps>) {
  const [field, meta, helpers] = useField(props);
  const { setValue } = helpers;
  const { values } = useFormikContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const transformedValue = toUpperCase ? value.toUpperCase() : value;
    setValue(transformedValue);
    
    if (onChange) {
      onChange(transformedValue);
    }
  };

  const renderInput = () => {
    const { type } = props;
    
    if (type === "checkbox") {
      return <RenderCheckbox field={field} meta={meta} label={label} isMobile={isMobile} {...props} />;
    }
    
    if (type === "radio") {
      return <RenderRadio 
        field={field} 
        meta={meta} 
        label={label}
        isMobile={isMobile}
        opciones={props.opciones}
        name={props.name}
        values={values}
        handleRadioChange={handleRadioChange}
      />;
    }
    
    if (type === "select") {
      return <RenderSelect 
        field={field} 
        meta={meta} 
        label={label}
        isMobile={isMobile}
        multiple={props.multiple}
        opciones={props.opciones}
        InputProps={props.InputProps}
        handleSelectChange={onChange}
      />;
    }
    
    // Default to text field for all other types (text, date, email, textarea, etc.)
    return <RenderTextField 
      field={field} 
      meta={meta} 
      label={label}
      isMobile={isMobile}
      type={type}  // Pasamos el tipo exacto (date, text, email, etc.)
      toUpperCase={toUpperCase}
      InputProps={props.InputProps}
      InputLabelProps={props.InputLabelProps}
      handleChange={handleTextChange}
    />;
  };

  return (
    <Box 
      paddingY={isMobile ? 0.5 : 1} 
      width="100%"
    >
      {renderInput()}
    </Box>
  );
}

export default TextInput;